import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import styles from "./Listening.module.css";

/**
 * Listening-history import.
 *
 * The library import answers *what do you own*. This answers *what do you actually play*, and it
 * is the only thing in the app nobody can type in: a song rated 8 four years ago and played twice
 * since is a different fact from a song rated 8 and played four hundred times.
 *
 * Same order as the library importer, for the same reason: choose, read what will happen, then
 * apply. The number that decides whether to go on is the **match rate** — a history that matches
 * two hundred of two thousand tracks means the library and the history are about different music,
 * and that is worth finding out before a hundred thousand rows land.
 */

export interface HistorySummary {
  format: "extended" | "account" | "unknown";
  plays: number;
  streams: number;
  distinctTracks: number;
  hours: number;
  firstAt: string | null;
  lastAt: string | null;
  nonMusic: number;
  unusable: number;
  empty: number;
  matchedTracks: number;
  matchedPlays: number;
  unmatchedTracks: number;
  topUnmatched: { artist: string; title: string; plays: number }[];
  /** Track keys, so counts across several files are a union rather than a sum. */
  keys: string[];
  matchedKeys: string[];
}

export interface HistoryImported extends HistorySummary {
  inserted: number;
  alreadyPresent: number;
  newlyLinked: number;
}

export interface UnratedFavourite {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  plays: number;
}

export interface ListeningProps {
  /** What the library already knows, so a second import is not described as a first. */
  status: { plays: number; tracks: number; matchedTracks: number; firstAt: string | null; lastAt: string | null } | null;
  onPreview: (file: File) => Promise<HistorySummary>;
  onImport: (file: File) => Promise<HistoryImported>;
  loadUnrated: () => Promise<UnratedFavourite[]>;
}

type Step = "choose" | "reading" | "review" | "importing" | "done";

const n = (value: number) => value.toLocaleString();

const monthYear = (iso: string | null) => {
  if (iso === null) return "—";
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${M[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** One summary out of many files, added up the way the server would have if it had seen them all. */
function merge(parts: HistorySummary[]): HistorySummary {
  const first = parts.map((p) => p.firstAt).filter((v): v is string => v !== null).sort()[0] ?? null;
  const last = parts.map((p) => p.lastAt).filter((v): v is string => v !== null).sort().at(-1) ?? null;

  /*
   * A Spotify download is split by year and the same song appears in several of its files, so
   * adding per-file counts would report a song played across four years as four tracks. These are
   * unions: the counts below are of the SET, not of the files.
   */
  const keys = new Set(parts.flatMap((p) => p.keys));
  const matchedKeys = new Set(parts.flatMap((p) => p.matchedKeys));

  const unmatched = new Map<string, { artist: string; title: string; plays: number }>();
  for (const part of parts) {
    for (const track of part.topUnmatched) {
      const key = `${track.artist}|${track.title}`;
      const existing = unmatched.get(key);
      if (existing) existing.plays += track.plays;
      else unmatched.set(key, { ...track });
    }
  }

  return {
    format: parts.find((p) => p.format === "extended")?.format ?? parts[0]?.format ?? "unknown",
    plays: parts.reduce((t, p) => t + p.plays, 0),
    streams: parts.reduce((t, p) => t + p.streams, 0),
    distinctTracks: keys.size,
    hours: Math.round(parts.reduce((t, p) => t + p.hours, 0) * 10) / 10,
    firstAt: first,
    lastAt: last,
    nonMusic: parts.reduce((t, p) => t + p.nonMusic, 0),
    unusable: parts.reduce((t, p) => t + p.unusable, 0),
    empty: parts.reduce((t, p) => t + p.empty, 0),
    matchedTracks: matchedKeys.size,
    matchedPlays: parts.reduce((t, p) => t + p.matchedPlays, 0),
    unmatchedTracks: keys.size - matchedKeys.size,
    // Each file reported its own twelve; the same track across four years is one entry carrying
    // all four years' plays, which is the number that makes it a decision rather than a trivium.
    topUnmatched: [...unmatched.values()].sort((a, b) => b.plays - a.plays).slice(0, 12),
    keys: [...keys],
    matchedKeys: [...matchedKeys],
  };
}

export function Listening({ status, onPreview, onImport, loadUnrated }: ListeningProps) {
  const [step, setStep] = useState<Step>("choose");
  const [files, setFiles] = useState<File[]>([]);
  const [summary, setSummary] = useState<HistorySummary | null>(null);
  const [imported, setImported] = useState<HistoryImported | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [unrated, setUnrated] = useState<UnratedFavourite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const choose = async (chosen: File[]) => {
    setError(null);
    setFiles(chosen);
    setStep("reading");
    setProgress({ done: 0, total: chosen.length });
    try {
      const parts: HistorySummary[] = [];
      for (const file of chosen) {
        parts.push(await onPreview(file));
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
      setSummary(merge(parts));
      setStep("review");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the file could not be read");
      setStep("choose");
    }
  };

  const apply = async () => {
    setStep("importing");
    setProgress({ done: 0, total: files.length });
    try {
      const parts: HistoryImported[] = [];
      // One file at a time, deliberately. An extended export is tens of megabytes per file and
      // there are usually a dozen; sending them together would be one request that either holds
      // the lot in memory or fails whole.
      for (const file of files) {
        parts.push(await onImport(file));
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
      setImported({
        ...merge(parts),
        inserted: parts.reduce((t, p) => t + p.inserted, 0),
        alreadyPresent: parts.reduce((t, p) => t + p.alreadyPresent, 0),
        newlyLinked: parts.reduce((t, p) => t + p.newlyLinked, 0),
      });
      setUnrated(await loadUnrated().catch(() => []));
      setStep("done");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "the import failed");
      setStep("review");
    }
  };

  return (
    <div className={styles.page} data-testid="listening-import">
      <div className={styles.head}>
        <div className={styles.title}>Listening history</div>
        <Link to="/import" className={styles.back} data-testid="go-library-import">
          ← Library import
        </Link>
      </div>

      {step === "choose" && (
        <div className={styles.intro}>
          <p className={styles.lede}>
            Your ratings say what you <b>think</b>. This says what you <b>did</b> — and it is the one thing about a
            collection nobody can type in.
          </p>
          <p className={styles.note}>
            Ask Spotify for your data under <b>Privacy Settings → Extended streaming history</b>, then choose every
            <code> Streaming_History_Audio_*.json </code> in the download. The twelve-month{" "}
            <code>StreamingHistory*.json</code> files work too. Nothing but the track, the time and the milliseconds is
            read — no addresses, no devices.
          </p>

          {status !== null && status.plays > 0 && (
            <div className={styles.already} data-testid="listening-already">
              ALREADY IMPORTED · {n(status.plays)} PLAYS · {n(status.matchedTracks)} OF {n(status.tracks)} TRACKS
              MATCHED · {monthYear(status.firstAt)} → {monthYear(status.lastAt)}
              <div className={styles.alreadyNote}>
                Importing again is safe: a stream already stored is recognised and skipped.
              </div>
            </div>
          )}

          <input
            ref={input}
            type="file"
            accept=".json,application/json"
            multiple
            className={styles.file}
            data-testid="history-files"
            onChange={(event) => {
              const chosen = [...(event.target.files ?? [])];
              if (chosen.length > 0) void choose(chosen);
            }}
          />
          {error && (
            <div className={styles.error} data-testid="listening-error">
              {error}
            </div>
          )}
          <button type="button" className={styles.primary} onClick={() => input.current?.click()}>
            Choose the JSON files
          </button>
        </div>
      )}

      {step === "reading" && (
        <div className={styles.working} data-testid="listening-reading">
          READING {progress.done + 1} OF {progress.total}…
        </div>
      )}

      {(step === "review" || step === "importing") && summary && (
        <div className={styles.review}>
          <div className={styles.summary} data-testid="history-summary">
            <div className={styles.counts}>
              <span data-testid="history-plays">{n(summary.plays)} PLAYS</span>
              <span className={styles.dot}>·</span>
              <span>{n(summary.hours)} HOURS</span>
            </div>
            {/* The span sits under the counts rather than beside them: three items at 40px wrap,
                and a wrapped separator reads as a typo. */}
            <div className={styles.range} data-testid="history-range">
              {monthYear(summary.firstAt)} → {monthYear(summary.lastAt)}
            </div>

            {/* The number that decides whether to go on. */}
            <div className={styles.match} data-testid="history-match">
              <div className={styles.matchTrack}>
                <div
                  className={styles.matchFill}
                  data-low={summary.matchedTracks / Math.max(1, summary.distinctTracks) < 0.5 || undefined}
                  style={{
                    width: `${Math.round((summary.matchedTracks / Math.max(1, summary.distinctTracks)) * 100)}%`,
                  }}
                />
              </div>
              <div className={styles.matchLabel}>
                {n(summary.matchedTracks)} OF {n(summary.distinctTracks)} STREAMED TRACKS ARE IN YOUR LIBRARY
              </div>
            </div>

            <ul className={styles.receipt}>
              {summary.streams > summary.plays && (
                <li>
                  {n(summary.streams - summary.plays)} streams stopped inside thirty seconds — stored, and not counted
                  as plays
                </li>
              )}
              {summary.nonMusic > 0 && <li data-testid="history-podcasts">{n(summary.nonMusic)} podcast or audiobook streams, left alone</li>}
              {summary.unusable > 0 && <li>{n(summary.unusable)} rows Spotify lost the metadata for</li>}
            </ul>
          </div>

          {summary.topUnmatched.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Played a lot, and not in your library</h2>
              {/* Named rather than merely counted: "169 unmatched" is a number; this is a decision
                  about whether the library is missing something. */}
              <div className={styles.unmatched} data-testid="history-unmatched">
                {summary.topUnmatched.map((track) => (
                  <div key={`${track.artist}|${track.title}`} className={styles.unmatchedRow}>
                    <span className={styles.unmatchedPlays}>{n(track.plays)}</span>
                    <span className={styles.unmatchedTitle}>{track.title}</span>
                    <span className={styles.unmatchedArtist}>{track.artist}</span>
                  </div>
                ))}
              </div>
              <p className={styles.note}>
                These stay in the history either way. If one of them belongs in the library, add the record from search
                and the next import attaches its plays.
              </p>
            </section>
          )}

          {error && (
            <div className={styles.error} data-testid="listening-error">
              {error}
            </div>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              data-testid="history-apply"
              disabled={step === "importing"}
              onClick={() => void apply()}
            >
              {step === "importing"
                ? `Importing ${progress.done + 1} of ${progress.total}…`
                : `Import ${n(summary.plays)} plays`}
            </button>
            <button
              type="button"
              className={styles.secondary}
              data-testid="history-cancel"
              disabled={step === "importing"}
              onClick={() => {
                setSummary(null);
                setFiles([]);
                setStep("choose");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === "done" && imported && (
        <div className={styles.done} data-testid="history-done">
          <div className={styles.counts}>
            <span data-testid="history-inserted">{n(imported.inserted)} STREAMS STORED</span>
            <span className={styles.dot}>·</span>
            <span>{n(imported.matchedTracks)} TRACKS MATCHED</span>
          </div>

          <ul className={styles.receipt}>
            {imported.alreadyPresent > 0 && (
              <li data-testid="history-duplicates">
                {n(imported.alreadyPresent)} streams were already stored, from an earlier import
              </li>
            )}
            {imported.unmatchedTracks > 0 && (
              <li>{n(imported.unmatchedTracks)} streamed tracks have nothing in the library to attach to</li>
            )}
          </ul>

          {unrated.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>You keep playing these, and have never rated them</h2>
              <div className={styles.unmatched} data-testid="unrated-favourites">
                {unrated.map((song) => (
                  <div key={song.id} className={styles.unmatchedRow}>
                    <span className={styles.unmatchedPlays}>{n(song.plays)}</span>
                    <span className={styles.unmatchedTitle}>{song.title}</span>
                    <span className={styles.unmatchedArtist}>{song.artist}</span>
                  </div>
                ))}
              </div>
              <Link to="/rate" search={{ queue: "most-played" }} className={styles.primary} data-testid="rate-most-played">
                Rate these →
              </Link>
            </section>
          ) : (
            <p className={styles.lede}>
              Everything the history could attach to is already rated. The plays are still stored — lists and playlists
              can read them now.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
